import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { Badge } from '@wds/badge';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { getCookie } from 'sma.common/utils';
import { ConsentModal, AccountPickerModal } from './DocuSignModals';
import docusignLogo from './docusign-logo.svg';

// --- Shared / Original Icons ---
const ExternalLinkIcon = () => (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
        <path
            d='M6.5 3H3.75a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75V10'
            stroke='currentColor'
            strokeWidth='1.3'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
        <path
            d='M9 3h4v4M13 3 7 9'
            stroke='currentColor'
            strokeWidth='1.3'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

// --- New Icon for Upgrade View ---
const LockIcon = () => (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
        <path d='M7 11V7a5 5 0 0 1 10 0v4' />
    </svg>
);

// --- Main Component ---
const DocuSignPromoCard = ({ connectUrl, isPlanBelowLimit, upgradeUrl, connectionsUrl, assignUrl }) => {
    const [siteConnections, setSiteConnections] = useState([]);
    const [modal, setModal] = useState(null); // null | 'consent' | 'picker'
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState('');

    // Fetch site connections on mount (if connectionsUrl provided) so the picker
    // knows whether to show "use existing" or go straight to OAuth.
    useEffect(() => {
        if (!connectionsUrl) return;
        fetch(connectionsUrl, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => { setSiteConnections(data.connections || []); })
            .catch(() => { setSiteConnections([]); });
    }, [connectionsUrl]);

    const handleConnectClick = useCallback(() => {
        if (siteConnections.length > 0) {
            setModal('picker');
        } else {
            setModal('consent');
        }
    }, [siteConnections]);

    const handleProceedOAuth = () => {
        window.location.href = connectUrl;
    };

    const handleContinueWithExisting = async (connectionId) => {
        if (!assignUrl || !connectionId) return;
        setAssigning(true);
        setAssignError('');
        try {
            const resp = await fetch(assignUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({ connection_id: connectionId }),
            });
            if (resp.ok) {
                setModal(null);
                window.location.reload();
            } else {
                const data = await resp.json().catch(() => ({}));
                setAssignError(data.message || gettext('Failed to assign account. Please try again.'));
            }
        } catch (_e) {
            setAssignError(gettext('Failed to assign account. Please try again.'));
        } finally {
            setAssigning(false);
        }
    };

    // When connectionsUrl is not provided (e.g. standalone usage), fall back to
    // direct navigation (original behaviour).
    const connectButton = isPlanBelowLimit ? (
        <Button color='upgrade' href={upgradeUrl}>
            <LockIcon />
            <span style={{ display: 'inline-block', width: 6 }} />
            {gettext('Upgrade')}
        </Button>
    ) : connectionsUrl ? (
        // Picker-aware path: show consent/picker modal
        <Button color='primary' onClick={handleConnectClick} stretched>
            {gettext('Connect to Docusign')}
            <span style={{ display: 'inline-block', width: 10 }} />
            <ExternalLinkIcon />
        </Button>
    ) : (
        // Fallback: direct OAuth navigation (no connectionsUrl provided)
        <Button color='primary' href={connectUrl} stretched>
            {gettext('Connect to Docusign')}
            <span style={{ display: 'inline-block', width: 10 }} />
            <ExternalLinkIcon />
        </Button>
    );

    const cardBg = isPlanBelowLimit ? '#FEF2CC' : '#E7F6F5';

    return (
        <>
            {modal === 'consent' && (
                <ConsentModal
                    onProceed={handleProceedOAuth}
                    onClose={() => setModal(null)}
                />
            )}
            {modal === 'picker' && (
                <AccountPickerModal
                    connections={siteConnections}
                    assigning={assigning}
                    onContinueWithExisting={handleContinueWithExisting}
                    onSignWithNew={() => setModal('consent')}
                    onClose={() => setModal(null)}
                />
            )}
            {assignError && (
                <div style={{
                    background: '#FFF8F7', border: '1px solid #FCECEA',
                    borderRadius: 4, padding: '8px 12px', marginBottom: 8,
                    fontSize: 13, color: '#D82C0D',
                }}>
                    {assignError}
                </div>
            )}
            <Box
                className={isPlanBelowLimit ? 'docusign-promo-card-upgrade' : 'docusign-promo-card'}
                style={{
                    backgroundColor: cardBg,
                    border: isPlanBelowLimit ? `1px solid ${cardBg}` : '1px solid #ddd',
                    borderRadius: '2px',
                }}
                p={3}
                display='flex'
                flexDirection='row'
                gap={2}
            >
                <img src={docusignLogo} alt='' height={22} />
                <Box display='flex' flexDirection='column' style={{ flex: 1 }}>
                    <Box display='flex' flexDirection='row' alignItems='center' gap={2} mb={2}>
                        <Typography variant='body' weight='medium' component='span'>
                            {gettext('Introducing Docusign')}
                        </Typography>
                        <Badge color='success' size='sm'>{gettext('New')}</Badge>
                    </Box>
                    <Box mb={3}>
                        <Typography variant='body'>
                            {isPlanBelowLimit
                                ? gettext('Collect signatures directly from your forms.')
                                : gettext('Connect to collect signatures directly from your forms.')}
                        </Typography>
                    </Box>
                    <Box>
                        {connectButton}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

DocuSignPromoCard.propTypes = {
    connectUrl: PropTypes.string,
    upgradeUrl: PropTypes.string,
    isPlanBelowLimit: PropTypes.bool,
    connectionsUrl: PropTypes.string,
    assignUrl: PropTypes.string,
};

DocuSignPromoCard.defaultProps = {
    isPlanBelowLimit: false,
    connectionsUrl: '',
    assignUrl: '',
};

export default DocuSignPromoCard;
